import { useEffect, useState } from "react";
import { mountReveal } from "../lib/bezpart/reveal";
import { Link, Navigate } from "react-router-dom";
import { BezpartHeader } from "../components/BezpartHeader";
import { HomeBoardDemo } from "../components/HomeBoardDemo";
import { HomeLessonScene } from "../components/HomeLessonScene";
import { apiFetch, type MeResponse } from "../api/client";
import { formatCabinetUserName } from "../lib/mainSiteLinks";
import { usePageMeta } from "../lib/pageMeta";
import "../styles/home-air.css";

/* ВИДЖЕТЫ ДОСКИ. Взяты из самого редактора (BoardWidgetsFlyout и
   packages/shared/boardTemplates): показываем ровно то, что на доске есть.
   У каждого — `why`: чему виджет учит на уроке. Это не украшение текста:
   доску покупают не ради кнопок, а ради того, что после урока ребёнок
   умеет больше. Обещать то, чего виджет не делает, нельзя. */
const WIDGETS = [
  {
    id: "timer",
    title: "Таймер",
    short: "Обратный отсчёт 1–10 минут",
    text: "Ставится прямо на холст и виден обоим. Нужен, когда даёте время на самостоятельное решение: ребёнок видит, сколько осталось, и вы не подгоняете его голосом.",
    why: "Учит распределять время — то, на чём теряют баллы на экзамене чаще, чем на незнании темы. Ребёнок привыкает, что на задачу есть минуты, а не «сколько получится», и перестаёт застревать на первом номере.",
  },
  {
    id: "calc",
    title: "Калькулятор",
    short: "Научный счёт",
    text: "Корни, степени, тригонометрия, скобки. Не нужно искать калькулятор в телефоне и терять доску из виду.",
    why: "Калькулятором тоже надо уметь пользоваться, и этому почти нигде не учат. На физике ребёнок встаёт в тупик не перед формулой, а перед тем, куда нажать: как считать синус в градусах, зачем скобки, почему степень даёт не то. Он на доске, вы видите каждое нажатие — и можете поправить руку, а не ответ.",
  },
  {
    id: "graph",
    title: "Графики",
    short: "Несколько функций, ОДЗ, точки",
    text: "Строит сразу несколько функций на одних осях, показывает область определения и отмечает точки. Видно, как меняется кривая, когда меняете коэффициент.",
    why: "Показывает то, что на бумаге приходится воображать: подвинули коэффициент — парабола поехала. За урок можно показать десяток случаев вместо одного аккуратно начерченного, и «как влияет a» становится увиденным, а не выученным.",
  },
  {
    id: "code",
    title: "Код",
    short: "Python · JS · HTML/CSS",
    text: "Редактор с подсветкой и запуском: программа выполняется прямо в карточке, вывод — под ней. Для занятий по программированию не нужна отдельная среда.",
    why: "Снимает главный барьер первого занятия — установку среды. Ребёнок пишет и запускает в том же окне, где вы рисуете схему алгоритма, и видит связь между нарисованным и работающим. Ошибку разбираете по выводу, а не по пересказу.",
  },
  {
    id: "sql",
    title: "SQL",
    short: "Базы данных, задания с проверкой",
    text: "Запросы к учебной базе прямо на доске, с проверкой ответа. Ученик видит и запрос, и результат, и где ошибся.",
    why: "Готовый курс внутри доски: больше ста заданий с подсказками, решением и проверкой — от SELECT до соединений. Вам не нужно держать базу и придумывать данные, а ученик учится на результате запроса, а не на словах «так правильно».",
  },
  {
    id: "words",
    title: "Слова",
    short: "Английский: слово и перевод",
    text: "Две колонки для словаря занятия. Заполняется по ходу урока и остаётся на доске вместе со всем остальным.",
    why: "Словарь набирается на самом занятии, в контексте, где слово встретилось, — и остаётся на доске к следующему разу. Не «вот список на дом», а то, что ребёнок видел и слышал, и к чему можно вернуться через неделю.",
  },
  {
    id: "poll",
    title: "Опрос",
    short: "Голосование на доске",
    text: "Вопрос с вариантами прямо на холсте. Удобно на групповом занятии: видно, кто как ответил, и можно разобрать спорный вариант.",
    why: "В группе показывает, кто на самом деле понял, а не кто первым поднял руку. Разбирать можно не правильный вариант, а тот, который выбрали, — именно он показывает, где сидит ошибка.",
  },
  {
    id: "pdf",
    title: "Презентация и PDF",
    short: "Файл становится слайдами",
    text: "PDF раскладывается на страницы с лентой слайдов, показ — во весь экран. Трансляция ведёт всех участников за вами, а слайд можно приблизить щипком на телефоне.",
    why: "Учебник, вариант или своя презентация попадают на доску как есть, и поверх них можно писать. Разбор задачи остаётся рядом с условием, а не в отдельной тетради, — и ученик потом открывает доску и видит ровно тот разбор.",
  },
  {
    id: "voice",
    title: "Голос и звук",
    short: "Голосовые сообщения и аудио",
    text: "Голосовое сообщение записывается прямо на доску, аудиофайл ставится как объект и слушается всеми.",
    why: "Нужен на языках: произношение не напишешь буквами. Можно оставить ребёнку голосовое с объяснением после урока — и он пересмотрит доску уже с вашим голосом, а не с памятью о нём.",
  },
] as const;

/* КОНСТРУКТОР ТАРИФА.
   ЦЕНЫ — МОЁ ПРЕДЛОЖЕНИЕ ПО РЫНКУ, а не решение школы. Сентябрь 2026,
   российские онлайн-доски (за пользователя в месяц): VK WorkSpace 175–207,
   Flip 499, МТС Линк от 499, MyBoards 500 за создателя, Элдоска от 585,
   Эсборд 624 при годовой оплате и 791 без неё.
   Логика: у нас платит ПРЕПОДАВАТЕЛЬ, ученики заходят по ссылке бесплатно —
   значит сравниваем с «за создателя», то есть с 500–585. База с одним-двумя
   модулями выходит дешевле любого из них, а полный набор — дороже Pro,
   и это нарочно: конструктор для тех, кому нужно две вещи, Pro для тех,
   кому нужно всё. */
/* База НЕ даёт безлимит досок, и это главное в ней.
   Пока давала, дешёвый конструктор был щедрее дорогого Pro по самому
   заметному лимиту: за 190 ₽ досок сколько угодно, за 490 ₽ — тридцать.
   Выбор между тарифами переставал существовать. Безлимит теперь только
   в Pro, а база — это десять досок и совместная работа. */
const CONSTRUCTOR_BASE = { title: "База «Доска»", price: 390, text: "10 досок и папки, ссылки для учеников, совместная работа" };
const CONSTRUCTOR_MODULES = [
  { id: "timer", title: "Таймер и опросы", price: 70 },
  { id: "calc", title: "Калькулятор", price: 70 },
  { id: "graph", title: "Графики функций", price: 90 },
  { id: "code", title: "Код: Python, JS, HTML/CSS", price: 150 },
  { id: "sql", title: "SQL и задания с проверкой", price: 150 },
  { id: "words", title: "Слова для английского", price: 70 },
  { id: "media", title: "PDF, картинки и аудио", price: 110 },
  { id: "snap", title: "Снимки доски", price: 90 },
  { id: "call", title: "Видеозвонок прямо в доске", price: 250 },
] as const;
/* Цена тарифа Pro: выше него собирать конструктор невыгодно, и страница
   говорит об этом прямо, а не ждёт, пока человек посчитает сам. */
const PRO_PRICE = 490;

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "0 ₽",
    period: "навсегда",
    blurb: "Чтобы провести первые занятия и понять, ваше ли это.",
    forWhom: "Преподавателю, который пробует онлайн-доску впервые",
    gives: "Полноценное занятие на доске: рисуете, пишете, разбираете задачу вместе. Этого хватает, чтобы понять, меняет ли доска ваш урок.",
    features: [
      "До 3 досок и 2 папок",
      "До 1500 объектов на доске — примерно два-три занятия",
      "Перо, фигуры, текст, стикеры",
      "Калькулятор, таймер, графики",
      "Доска сохраняется между занятиями",
      "Без гостевых ссылок и медиафайлов",
    ],
    cta: "Начать бесплатно",
    to: "/register",
    featured: false,
  },
  {
    id: "pro",
    name: "Pro",
    /* ЦЕНА — ПРЕДЛОЖЕНИЕ ПО РЫНКУ, не решение школы (см. разбор у
       CONSTRUCTOR_MODULES). Держать её «по запросу» было нельзя:
       конструктор ниже прямо зовёт «дешевле взять Pro за 490 ₽»,
       и карточка обязана называть то же число. */
    price: "490 ₽",
    period: "в месяц за преподавателя",
    blurb: "Рабочий инструмент репетитора: по доске на ученика и весь набор.",
    forWhom: "Репетитору с постоянными учениками и небольшим командам",
    gives: "Урок перестаёт быть разговором под камеру. У каждого ученика своя доска с историей занятий: он открывает её дома и видит ваш разбор, а не свои обрывочные записи. Это и есть то, за что берут дороже за час.",
    features: [
      "Доски и папки без лимита",
      "Ссылка ученику — без регистрации с его стороны",
      "PDF и презентации с показом на весь экран",
      "Все виджеты: SQL, код, графики, слова, опросы",
      "Голосовые сообщения и аудио",
      "Снимки доски: вернуться к состоянию любого занятия",
    ],
    cta: "Зарегистрироваться",
    to: "/register",
    featured: true,
  },
  {
    id: "business",
    name: "Business",
    price: "390 ₽",
    period: "в месяц за преподавателя, от 5",
    blurb: "Для школ и студий: общие материалы и единый порядок занятий.",
    forWhom: "Школам и студиям от пяти преподавателей",
    gives: "Методика перестаёт жить в головах: заготовки уроков лежат в общих папках, новый преподаватель начинает с готового материала, а не с чистого листа. Занятия у всех выглядят одинаково собранно — это видно родителю и держит цену школы.",
    features: [
      "Доски и папки без лимита",
      "Общие папки на команду, права на каждого",
      "Единые заготовки уроков для всех преподавателей",
      "Расширенная квота на файлы и записи",
      "Всё из Pro",
    ],
    cta: "Зарегистрироваться",
    to: "/register",
    featured: false,
  },
] as const;

/* УРОК В ТРИ ШАГА. Вместо сетки из шести «возможностей»: человек покупает
   не инструменты, а урок, и шаги рассказывают его по порядку. У каждого
   шага — маленький кусочек интерфейса вместо значка: так видно, как это
   выглядит, а не только что это умеет. */
const STEPS = [
  {
    n: "01",
    kicker: "Объяснить",
    title: "Всё под рукой на одном холсте",
    text: "Перо, фигуры, текст, PDF и графики функций. Доска открывается в браузере — ни вам, ни ученику ничего не нужно устанавливать.",
    mock: "tools",
  },
  {
    n: "02",
    kicker: "Решить вместе",
    title: "Ученик пишет на той же доске",
    text: "Вы видите его курсор и каждое действие. Ошибку разбираете там, где она появилась, а не по пересказу.",
    mock: "chat",
  },
  {
    n: "03",
    kicker: "Вернуться",
    title: "Урок остаётся на доске",
    text: "Доски лежат в кабинете по папкам. Ученик открывает свою дома и видит ваш разбор, а не обрывочные записи.",
    mock: "history",
  },
] as const;

const SUBJECTS = [
  "Математика",
  "Физика",
  "Информатика",
  "SQL",
  "Английский",
  "ОГЭ и ЕГЭ",
] as const;

/* Вопросы — только про то, что доска делает на самом деле (см. тарифы
   выше): обещать в ответах больше, чем в тарифах, нельзя. */
const FAQ = [
  {
    q: "Можно ли пользоваться бесплатно?",
    a: "Да. На тарифе Free — до трёх досок и двух папок, до 1500 объектов на доске. Этого хватает на первые занятия, чтобы понять, подходит ли вам доска.",
  },
  {
    q: "Нужно ли ученику регистрироваться?",
    a: "На тарифах Pro и Business — нет: вы отправляете ссылку, и ученик сразу оказывается на доске. Платит только преподаватель.",
  },
  {
    q: "Нужно ли что-то устанавливать?",
    a: "Нет. Доска работает в браузере на компьютере, планшете и телефоне.",
  },
  {
    q: "Можно ли показать PDF или презентацию?",
    a: "Да. Файл раскладывается на слайды, писать можно прямо поверх них, а показ во весь экран ведёт за вами всех участников.",
  },
  {
    q: "Что будет с доской после урока?",
    a: "Она остаётся в вашем кабинете. Доски раскладываются по папкам, а на Pro можно сохранить снимок доски и вернуться к любому занятию.",
  },
  {
    q: "Чем Pro отличается от конструктора?",
    a: "Pro — всё сразу и без лимита досок. Конструктор — для тех, кому нужны одна-две вещи: собираете только их и платите меньше.",
  },
] as const;

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Маленький кусочек интерфейса над шагом. */
function StepMock({ kind }: { kind: (typeof STEPS)[number]["mock"] }) {
  if (kind === "tools") {
    return (
      <ul className="wb-air-mock wb-air-mock--tools" aria-hidden="true">
        <li className="is-active">Перо</li>
        <li>Фигуры и стрелки</li>
        <li>Текст и стикеры</li>
        <li>PDF и презентации</li>
        <li>Графики функций</li>
      </ul>
    );
  }
  if (kind === "chat") {
    return (
      <div className="wb-air-mock wb-air-mock--chat" aria-hidden="true">
        <p>
          <span className="wb-air-mock__who">М</span>
          <span>
            <b>Мария Петровна</b>Попробуй через дискриминант
          </span>
        </p>
        <p className="is-student">
          <span className="wb-air-mock__who">А</span>
          <span>
            <b>Аня</b>D = 1, корни 2 и 3
          </span>
        </p>
        <span className="wb-air-mock__typing">
          <i />
          <i />
          <i />
        </span>
      </div>
    );
  }
  return (
    <ul className="wb-air-mock wb-air-mock--history" aria-hidden="true">
      <li className="is-active">
        <b>Квадратные уравнения</b>
        <span>сегодня, 16:40</span>
      </li>
      <li>
        <b>Теорема Виета</b>
        <span>14 октября</span>
      </li>
      <li>
        <b>Неравенства</b>
        <span>10 октября</span>
      </li>
    </ul>
  );
}

/**
 * Публичная витрина (SEO): тарифы и регистрация для внешних пользователей.
 * Школьный вход — отдельный маршрут /school (не индексируется).
 *
 * ВОЗДУШНАЯ ГЛАВНАЯ (октябрь 2026, по просьбе владельца «лёгкая,
 * ненавязчивая»). Что держит лёгкость, и что нельзя ломать:
 * — заголовки тонкие (Rubik 500) и строчными, вторая строка приглушённым
 *   фиолетовым; ЗАГЛАВНЫЕ и жирность 800 делали страницу громкой;
 * — один цвет-действие на экран: плоская фиолетовая кнопка, без градиента
 *   и тени; всё остальное — чернила и белое;
 * — разделы без карточек и теней, их разделяет воздух и тонкие линии;
 * — сцена урока в первом экране показывает доску вместо перечня функций.
 * Стили — в styles/home-air.css, классы wb-air-*: фирменный слой их
 * не трогает, и остальные страницы сайта от этой не зависят.
 */
export function HomePage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [ready, setReady] = useState(false);

  usePageMeta({
    title: "Bezpart Доска — онлайн-доска для занятий | тарифы",
    description:
      "Онлайн-доска для уроков и репетиторства: рисование, фигуры, PDF, совместная работа. Тарифы Free, Pro и Business. Регистрация без привязки к онлайн-школе.",
    robots: "index, follow",
  });

  /* Конструктор: что человек положил «в корзину». Состояние держим
     здесь, а не в адресе: выбор ни на что за пределами страницы
     не влияет, и делиться такой ссылкой незачем. */
  const [modules, setModules] = useState<string[]>([]);
  const toggleModule = (id: string) =>
    setModules((was) => (was.includes(id) ? was.filter((x) => x !== id) : [...was, id]));
  const total =
    CONSTRUCTOR_BASE.price +
    CONSTRUCTOR_MODULES.filter((m) => modules.includes(m.id)).reduce((a, m) => a + m.price, 0);

  useEffect(() => {
    document.body.classList.add("wb-page--home", "wb-page--public-landing", "wb-page--air");
    /* Моторика монтируется ЗДЕСЬ, а не при разборе файла: узлы страницы
       появляются после отрисовки, и скрипт, отработавший раньше,
       не нашёл бы ни одного блока. */
    const снять = mountReveal(document);
    return () => {
      снять();
      document.body.classList.remove("wb-page--home", "wb-page--public-landing", "wb-page--air");
    };
  }, []);

  useEffect(() => {
    void apiFetch<MeResponse>("/api/v1/me")
      .then((me) => {
        setLoggedIn(true);
        setUserName(formatCabinetUserName(me.email || ""));
      })
      .catch(() => setLoggedIn(false))
      .finally(() => setReady(true));
  }, []);

  if (ready && loggedIn) {
    return <Navigate to="/cabinet" replace />;
  }

  return (
    <>
      {loggedIn ? (
        <BezpartHeader mode="logged-in" userName={userName || "Пользователь"} />
      ) : (
        <BezpartHeader mode="guest" loginHref="/login" />
      )}

      <main className="wb-air">
        {/* ПЕРВЫЙ ЭКРАН */}
        <section className="wb-air-hero">
          <div className="wb-air-wrap">
            <p className="wb-air-badge">
              <i aria-hidden="true" />
              Работает в браузере — ничего не нужно устанавливать
            </p>
            <h1 className="wb-air-h1">
              Онлайн-доска
              <span>для живых занятий</span>
            </h1>
            <p className="wb-air-lead wb-air-hero__lead">
              Объясняйте, решайте задачи вместе с учеником и возвращайтесь к уроку, когда нужно.
              Всё на одном холсте.
            </p>
            <div className="wb-air-actions">
              <Link className="wb-air-btn" to="/register">
                Начать бесплатно
              </Link>
              <a className="wb-air-link" href="#try">
                Попробовать без регистрации
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>
          <div className="wb-air-wrap wb-air-wrap--wide">
            <HomeLessonScene />
          </div>
        </section>

        <section className="wb-air-subjects" aria-label="Предметы">
          <div className="wb-air-wrap">
            <p className="wb-air-subjects__label">Для уроков по</p>
            <ul>
              {SUBJECTS.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* УРОК В ТРИ ШАГА */}
        <section className="wb-air-section" aria-labelledby="wb-air-steps-title">
          <div className="wb-air-wrap">
            <div className="wb-air-head" data-reveal>
              <p className="wb-air-eyebrow">Как проходит занятие</p>
              <h2 id="wb-air-steps-title" className="wb-air-h2">
                Объяснили, решили вместе —
                <span>и всё осталось на доске.</span>
              </h2>
            </div>
            <ol className="wb-air-steps">
              {STEPS.map((s, i) => (
                <li key={s.n} className="wb-air-step" data-reveal data-reveal-delay={i * 90}>
                  <StepMock kind={s.mock} />
                  <p className="wb-air-step__kicker">
                    {s.n} <span aria-hidden="true">/</span> {s.kicker}
                  </p>
                  <h3 className="wb-air-step__title">{s.title}</h3>
                  <p className="wb-air-step__text">{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ПОПРОБОВАТЬ СРАЗУ. Доска — товар, который нельзя описать
            словами: пока на ней не порисуешь, непонятно, зачем она. */}
        <section className="wb-air-section wb-air-section--tint" id="try">
          <div className="wb-air-wrap">
            <HomeBoardDemo />
          </div>
        </section>

        {/* ВИДЖЕТЫ: строки с раскрытием вместо девяти карточек. Текст
            «что это даёт уроку» остался целиком — он спрятан, а не убран. */}
        <section className="wb-air-section" aria-labelledby="wb-air-widgets-title">
          <div className="wb-air-wrap wb-air-split">
            <div className="wb-air-split__aside" data-reveal>
              <p className="wb-air-eyebrow">Виджеты</p>
              <h2 id="wb-air-widgets-title" className="wb-air-h2">
                То, чего нет
                <span>в обычной доске.</span>
              </h2>
              <p className="wb-air-lead">
                Ставятся прямо на холст и остаются там вместе с уроком. Нажмите на строку —
                расскажем, что виджет даёт занятию.
              </p>
            </div>
            <ul className="wb-air-rows" data-reveal>
              {WIDGETS.map((w) => (
                <li key={w.id}>
                  <details className="wb-air-row">
                    <summary>
                      <span className="wb-air-row__title">{w.title}</span>
                      <span className="wb-air-row__short">{w.short}</span>
                      <span className="wb-air-row__plus" aria-hidden="true" />
                    </summary>
                    <div className="wb-air-row__body">
                      <p>{w.text}</p>
                      <p className="wb-air-row__why">
                        <span>Что это даёт уроку</span>
                        {w.why}
                      </p>
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ДЛЯ РЕПЕТИТОРА: сценарий словами, а не перечень функций. */}
        <section className="wb-air-section wb-air-section--tint" aria-labelledby="wb-air-tutor-title">
          <div className="wb-air-wrap wb-air-split">
            <div data-reveal>
              <p className="wb-air-eyebrow">Для репетитора</p>
              <h2 id="wb-air-tutor-title" className="wb-air-h2">
                Урок, после которого
                <span>есть к чему вернуться.</span>
              </h2>
            </div>
            <div className="wb-air-prose" data-reveal>
              <p>
                У каждого ученика своя доска. Перед занятием вы кладёте на неё задание или
                вариант в PDF, а на уроке разбираете прямо поверх условия — ученик пишет там же,
                и вы видите, где он ошибся.
              </p>
              <p>
                После урока доска никуда не девается. Ученик открывает её дома и видит ваш разбор
                целиком, а вы добавляете домашнее задание или оставляете голосовое с объяснением.
                К следующему занятию всё уже на месте.
              </p>
            </div>
          </div>
        </section>

        {/* ТАРИФЫ */}
        <section className="wb-air-section" id="pricing" aria-labelledby="wb-air-pricing-title">
          <div className="wb-air-wrap">
            <div className="wb-air-head" data-reveal>
              <p className="wb-air-eyebrow">Тарифы</p>
              <h2 id="wb-air-pricing-title" className="wb-air-h2">
                Платит преподаватель.
                <span>Ученики — бесплатно.</span>
              </h2>
              <p className="wb-air-lead">
                Начните с Free и перейдите выше, когда доска станет частью каждого занятия.
              </p>
            </div>
            <ul className="wb-air-plans" data-reveal>
              {PLANS.map((p) => (
                <li key={p.id} className={`wb-air-plan${p.featured ? " is-featured" : ""}`}>
                  <div className="wb-air-plan__top">
                    <h3 className="wb-air-plan__name">{p.name}</h3>
                    {p.featured ? <span className="wb-air-plan__tag">Популярный</span> : null}
                  </div>
                  <p className="wb-air-plan__blurb">{p.blurb}</p>
                  <p className="wb-air-plan__price">{p.price}</p>
                  <p className="wb-air-plan__period">{p.period}</p>
                  <Link
                    className={p.featured ? "wb-air-btn wb-air-btn--block" : "wb-air-btn wb-air-btn--ghost wb-air-btn--block"}
                    to={p.to}
                  >
                    {p.cta}
                  </Link>
                  <ul className="wb-air-plan__list">
                    {p.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <p className="wb-air-plan__who">{p.forWhom}</p>
                  <p className="wb-air-plan__gives">{p.gives}</p>
                </li>
              ))}
            </ul>

            {/* КОНСТРУКТОР ТАРИФА. Рядом с фиксированными тарифами, а не
                вместо них: кому нужно всё — берёт Pro, кому две вещи —
                собирает сам. */}
            <div className="wb-air-builder" data-reveal>
              <div className="wb-air-builder__head">
                <h3 className="wb-air-h3">Нужны только одна-две вещи?</h3>
                <p className="wb-air-lead">
                  Соберите свой тариф: база и то, чем будете пользоваться.
                </p>
              </div>
              <div className="wb-air-builder__body">
                <ul className="wb-air-builder__list">
                  <li className="wb-air-builder__base">
                    <span className="wb-air-builder__check is-fixed" aria-hidden="true" />
                    <span>
                      <b>{CONSTRUCTOR_BASE.title}</b>
                      <small>{CONSTRUCTOR_BASE.text}</small>
                    </span>
                    <span className="wb-air-builder__price">{CONSTRUCTOR_BASE.price} ₽</span>
                  </li>
                  {CONSTRUCTOR_MODULES.map((m) => {
                    const sel = modules.includes(m.id);
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          className={`wb-air-builder__row${sel ? " is-on" : ""}`}
                          aria-pressed={sel}
                          onClick={() => toggleModule(m.id)}
                        >
                          <span className="wb-air-builder__check" aria-hidden="true" />
                          <span>{m.title}</span>
                          <span className="wb-air-builder__price">+{m.price} ₽</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <aside className="wb-air-builder__sum" aria-live="polite">
                  <p className="wb-air-eyebrow">Ваш тариф</p>
                  <p className="wb-air-builder__total">
                    {total} <span>₽ в месяц</span>
                  </p>
                  <p className="wb-air-builder__note">
                    {modules.length === 0
                      ? "Пока только база. Отметьте, что нужно на занятиях."
                      : `База и ${modules.length} ${plural(modules.length, "модуль", "модуля", "модулей")}`}
                  </p>
                  {total >= PRO_PRICE ? (
                    <p className="wb-air-builder__hint">
                      Дешевле взять <a href="#pricing">Pro за {PRO_PRICE} ₽</a> — в нём уже есть всё сразу.
                    </p>
                  ) : null}
                  <Link className="wb-air-btn wb-air-btn--block" to="/register">
                    Начать с этим набором
                  </Link>
                </aside>
              </div>
            </div>
          </div>
        </section>

        {/* ВОПРОСЫ */}
        <section className="wb-air-section wb-air-section--tint" aria-labelledby="wb-air-faq-title">
          <div className="wb-air-wrap wb-air-split">
            <div className="wb-air-split__aside" data-reveal>
              <p className="wb-air-eyebrow">Вопросы</p>
              <h2 id="wb-air-faq-title" className="wb-air-h2">
                Перед первым
                <span>уроком.</span>
              </h2>
            </div>
            <ul className="wb-air-rows" data-reveal>
              {FAQ.map((f) => (
                <li key={f.q}>
                  <details className="wb-air-row">
                    <summary>
                      <span className="wb-air-row__title wb-air-row__title--q">{f.q}</span>
                      <span className="wb-air-row__plus" aria-hidden="true" />
                    </summary>
                    <div className="wb-air-row__body">
                      <p>{f.a}</p>
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ПОСЛЕДНИЙ ЭКРАН: одна фраза и два действия. */}
        <section className="wb-air-final">
          <div className="wb-air-wrap" data-reveal>
            <h2 className="wb-air-h2 wb-air-final__title">
              Попробуем
              <span>на следующем уроке?</span>
            </h2>
            <p className="wb-air-lead">Начните бесплатно: три доски, без установки программ.</p>
            <div className="wb-air-actions">
              <Link className="wb-air-btn" to="/register">
                Создать доску
              </Link>
              <Link className="wb-air-link" to="/login">
                У меня уже есть аккаунт
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
